import { useEffect, useState } from 'react';
import { NSButton, NSModal } from 'bricks';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { IOptionAnyValue } from 'bricks/types';

import useAuthContext from 'contexts/AuthContext';
import { useCreateProForma } from 'queries/ProForma';
import useProFormaModels from 'queries/ProForma/useProFormaModels';
import { URLS } from 'services/urls';
import { ProFormaModelIds } from 'constants/proForma';
import { getLocalStorage, storeLocalStorage } from 'helpers/localStorage/helpers';
import { GlobalKeys } from 'helpers/localStorage/constants';
import useAssetTypeDropdownOptions from 'hooks/useDisabledAssetTypesProps';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { DEAL_MIXED_USE_RESIDENTIAL_OVER_RETAIL } from 'views/ProFormaTable/constants';

import StartFromBoeSection from './components/StartFromBoeSection/StartFromBoeSection';
import StartFromScratchSection from './components/StartFromScratchSection/StartFromScratchSection';
import { useBoeScenarioSelection } from './hooks/useBoeScenarioSelection';

export interface ICreateProFormaModalProps {
    toggle: () => void;
    isOpen: boolean;
    initialScenario?: IOptionAnyValue;
    dealAssetTypeCorrelationId?: string;
    dealId?: number;
}

const CreateProFormaModal = ({
    toggle, isOpen, initialScenario, dealAssetTypeCorrelationId, dealId: selectedDealId,
}: ICreateProFormaModalProps) => {
    const { dealId: dealIdFromParams } = useParams<{ dealId: string }>();
    const dealId = selectedDealId ? String(selectedDealId) : dealIdFromParams;
    const numericDealId = Number(dealId);
    const { selectedOrganizationId, user } = useAuthContext();
    const navigate = useNavigate();

    const shouldDefaultToApartmentWithRetail = dealAssetTypeCorrelationId === DEAL_MIXED_USE_RESIDENTIAL_OVER_RETAIL;
    const isNorthspyreUser = Boolean(user?.email?.toLowerCase().endsWith('@northspyre.com'));

    const [proFormaModelId, setProFormaModelId] = useState<number | undefined>(undefined);
    const [selectedScenario, setSelectedScenario] = useState<IOptionAnyValue | undefined>(initialScenario);
    const [shouldStartFromBackOfEnvelope, setShouldStartFromBackOfEnvelope] = useState(!shouldDefaultToApartmentWithRetail);

    const { data: deal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: numericDealId,
        shouldFetch: Boolean(numericDealId && selectedOrganizationId),
    });

    const { data: assetTypes } = useProFormaModels();
    const assetTypeDropdownOptions = useAssetTypeDropdownOptions(assetTypes || []);

    const {
        scenariosData, fullSelectedScenario, scenarioDropdownOptions,
        returnOnCost, returnOnEquity, profitReturn, openSlideout,
    } = useBoeScenarioSelection({
        orgId: selectedOrganizationId!,
        dealId: numericDealId,
        selectedScenario,
        shouldStartFromBackOfEnvelope,
    });

    useEffect(() => {
        if (!scenariosData?.items) return;
        if (!scenariosData.items.length) {
            setSelectedScenario(undefined);
            return;
        }
        const currentScenarioExists = selectedScenario && scenariosData.items.some(s => s.id === selectedScenario.value);
        if (!currentScenarioExists && initialScenario) {
            setSelectedScenario({ label: scenariosData.items[0].name, value: scenariosData.items[0].id });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenariosData?.items, initialScenario]);

    useEffect(() => {
        if (fullSelectedScenario?.propertyTypeId && shouldStartFromBackOfEnvelope) {
            const propertyTypeId = fullSelectedScenario.propertyTypeId === ProFormaModelIds.APARTMENT_WITH_RETAIL && !isNorthspyreUser
                ? ProFormaModelIds.APARTMENT
                : fullSelectedScenario.propertyTypeId;
            setProFormaModelId(propertyTypeId);
        }
        if (!shouldStartFromBackOfEnvelope && shouldDefaultToApartmentWithRetail) {
            setProFormaModelId(isNorthspyreUser ? ProFormaModelIds.APARTMENT_WITH_RETAIL : ProFormaModelIds.APARTMENT);
            setSelectedScenario(undefined);
        }
    }, [fullSelectedScenario, shouldStartFromBackOfEnvelope, shouldDefaultToApartmentWithRetail, isNorthspyreUser]);

    const { mutate: createProForma, isPending: isCreatingProForma } = useCreateProForma();

    const handleCreateProForma = () => {
        createProForma(
            {
                dealId: numericDealId,
                selectedOrganizationId: String(selectedOrganizationId),
                scenarioId: selectedScenario?.value ? Number(selectedScenario.value) : undefined,
                proFormaModelId,
            },
            {
                onSuccess: data => {
                    if (data.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL) {
                        const ids = getLocalStorage<number[]>(GlobalKeys.ApartmentRetailReviewBadgeProformaIds) || [];
                        storeLocalStorage(GlobalKeys.ApartmentRetailReviewBadgeProformaIds, [...ids, data.id]);
                    }
                    navigate(generatePath(URLS.PRO_FORMA.TABLE, { dealId, proFormaId: data.id.toString() }));
                },
                onError: error => { console.error('Error creating pro forma:', error); },
            },
        );
    };

    const handleSelectScenario = (option: IOptionAnyValue) => {
        if (option?.value === 'no-back-of-envelope') return;
        setSelectedScenario(option);
    };

    const handleSelectAssetType = (option: IOptionAnyValue) => {
        setProFormaModelId(option?.value);
        setSelectedScenario(undefined);
        setShouldStartFromBackOfEnvelope(false);
    };

    const isConfirmDisabled = (shouldStartFromBackOfEnvelope && !selectedScenario) || (!shouldStartFromBackOfEnvelope && !proFormaModelId);

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderClassName="bg-primary modal-colored-header"
            modalHeaderText="Generate pro forma"
            modalFooterIsShown={false}
            size="lg"
        >
            <StartFromBoeSection
                isSelected={shouldStartFromBackOfEnvelope}
                onSelect={() => setShouldStartFromBackOfEnvelope(true)}
                scenarioDropdownOptions={scenarioDropdownOptions}
                selectedScenario={selectedScenario}
                onSelectScenario={handleSelectScenario}
                returnOnCost={returnOnCost}
                returnOnEquity={returnOnEquity}
                profitReturn={profitReturn}
                targetReturnOnCost={deal?.targetReturnOnCost ?? null}
                targetReturnOnEquity={deal?.targetReturnOnEquity ?? null}
                targetProfitReturn={deal?.targetProfitReturn ?? null}
                onOpenSlideout={openSlideout}
            />

            <StartFromScratchSection
                isSelected={!shouldStartFromBackOfEnvelope}
                onSelect={() => { setSelectedScenario(undefined); setShouldStartFromBackOfEnvelope(false); }}
                assetTypeOptions={assetTypeDropdownOptions}
                proFormaModelId={proFormaModelId}
                onSelectAssetType={handleSelectAssetType}
            />

            <hr />

            <div className="d-flex float-right my-0">
                <NSButton outline callback={toggle} text="Cancel" className="btn-secondary mr-2" disabled={false} />
                <NSButton
                    outline={false}
                    callback={handleCreateProForma}
                    className="btn-primary btn-block"
                    text="Confirm"
                    disabled={isConfirmDisabled}
                    isLoading={isCreatingProForma}
                    dataTestId="confirm-pro-forma-button"
                />
            </div>
        </NSModal>
    );
};

export default CreateProFormaModal;
